import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { EntityCard } from "components/common/EntityCard";
import { InfiniteScrollContainer } from "components/common/InfiniteScrollContainer";
import api from "utils/api";
import { getStatusColor } from "utils/documentation/getStatusColor";
import { formatDate } from "utils/documentation/formatDate";
import { DocumentationGridProps } from "./types";

const defaultOnLoadMore = () => {};

export const DocumentationGrid = ({
  projectId,
  documentations,
  isLoading = false,
  canLoadMore = false,
  onLoadMore = defaultOnLoadMore,
}: DocumentationGridProps) => {
  const { t } = useTranslation();

  return (
    <div>
      <div className="mt-3 mb-3">
        <h1 className="text-2xl font-bold text-gray-900">
          {t("tabs.documentations")}
        </h1>
        <p className="text-gray-600 mt-1">
          {t("documentation.found", {
            count: documentations?.length || 0,
          })}
        </p>
      </div>
      {!documentations || documentations.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">{t("documentation.not_available")}</p>
        </div>
      ) : (
        <InfiniteScrollContainer
          onEndReached={onLoadMore}
          isActive={canLoadMore}
          isLoading={isLoading}
          rootMargin="50px"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {documentations.map((documentation) => (
              <EntityCard
                key={documentation.id}
                as={Link}
                to={`/project/${projectId}/sections/${documentation.section.id}/documentation/${documentation.id}`}
                title={t("documentation.documentation_id", {
                  id: documentation.id,
                })}
                subtitle={t("documentation.section_name", {
                  section: documentation.section.name,
                })}
                imageUrl={api.getDocumentationPreviewImageUrl({
                  documentationId: documentation.id,
                  documentationPreviewPath: documentation.previewPath,
                })}
                imageAlt={t("documentation.map_thumbnail_alt", {
                  id: documentation.id,
                })}
                imageFit="contain"
                imageFallback={t("documentation.no_image")}
                status={{
                  text: documentation.status.replace("_", " "),
                  className: getStatusColor({ status: documentation.status }),
                }}
                chips={[
                  {
                    label: t("documentation.frames"),
                    value: documentation.frameCount,
                  },
                  {
                    label: t("documentation.objects"),
                    value: documentation.totalObjectCount,
                  },
                ]}
                createdAt={formatDate({ date: documentation.createdAt })}
                createdAtLabel={t("documentation.created_at")}
              />
            ))}
          </div>
        </InfiniteScrollContainer>
      )}
    </div>
  );
};
